import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Shield, Wrench, CheckCircle, X, Eye, EyeOff, Sparkles } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface AnalysisItem {
  imageUrl: string;
  analysis: any;
  approved?: boolean;
  useForTicket?: boolean;
  saveAsImageDesc?: boolean;
  editedDescription?: string;
}

interface AnalysisReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  analyses: AnalysisItem[];
  onSaveSelected: (analyses: AnalysisItem[]) => void | Promise<void>;
  onIgnoreAll: () => void;
}

export default function AnalysisReviewDialog({
  isOpen,
  onClose,
  analyses,
  onSaveSelected,
  onIgnoreAll,
}: AnalysisReviewDialogProps) {
  const [localAnalyses, setLocalAnalyses] = useState<AnalysisItem[]>(analyses);
  const [useAllForTicket, setUseAllForTicket] = useState(false);
  const { t } = useTranslation(['components.imageAnalysis']);

  // Update local state when analyses prop changes
  useEffect(() => {
    setLocalAnalyses(analyses.map(a => ({
      ...a,
      editedDescription: a.editedDescription || a.analysis?.analysisText || '',
      saveAsImageDesc: a.saveAsImageDesc !== undefined ? a.saveAsImageDesc : true, // Default to true if not set
    })));
  }, [analyses]);

  // Update all analyses when "Use all for ticket" is toggled
  useEffect(() => {
    if (useAllForTicket) {
      setLocalAnalyses(prev => prev.map(a => ({ ...a, useForTicket: true })));
    } else {
      // When unchecked, don't automatically uncheck all - let user decide individually
    }
  }, [useAllForTicket]);

  const getRiskLevelColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'low':
      case 'niedrig':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'medium':
      case 'mittel':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'high':
      case 'hoch':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'critical':
      case 'kritisch':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRiskIcon = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'low':
      case 'niedrig':
        return <CheckCircle className="w-4 h-4" />;
      case 'medium':
      case 'mittel':
        return <Eye className="w-4 h-4" />;
      case 'high':
      case 'hoch':
        return <AlertTriangle className="w-4 h-4" />;
      case 'critical':
      case 'kritisch':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  const handleDescriptionChange = (index: number, description: string) => {
    setLocalAnalyses(prev => prev.map((a, i) => 
      i === index ? { ...a, editedDescription: description } : a
    ));
  };

  const handleUseForTicketChange = (index: number, checked: boolean) => {
    setLocalAnalyses(prev => prev.map((a, i) => 
      i === index ? { ...a, useForTicket: checked } : a
    ));
    if (!checked) {
      setUseAllForTicket(false);
    }
  };

  const handleSaveAsImageDescChange = (index: number, checked: boolean) => {
    setLocalAnalyses(prev => prev.map((a, i) => 
      i === index ? { ...a, saveAsImageDesc: checked } : a
    ));
  };

  const handleRemoveAnalysis = (index: number) => {
    setLocalAnalyses(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveSelected = async () => {
    await onSaveSelected(localAnalyses);
    onClose();
  };

  const handleIgnoreAll = () => {
    onIgnoreAll();
    onClose();
  };

  // Filter out analyses without analysis data
  const validAnalyses = localAnalyses.filter(a => a.analysis !== null && a.analysis !== undefined);
  
  // Get highest risk level from all valid analyses
  const highestRisk = validAnalyses.length > 0 
    ? validAnalyses.reduce((highest, item) => {
        const risk = item.analysis?.riskLevel || 'medium';
        const riskOrder = { low: 1, medium: 2, high: 3, critical: 4 };
        return (riskOrder[risk as keyof typeof riskOrder] || 2) > (riskOrder[highest as keyof typeof riskOrder] || 2) 
          ? risk 
          : highest;
      }, 'medium' as string)
    : 'medium';
  
  if (validAnalyses.length === 0) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 gap-0 bg-white">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {t('components.imageAnalysis:review.title') || 'Review AI Analyses'}
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                {t('components.imageAnalysis:review.subtitle') || 'Review and select which analyses to use'}
              </p>
            </div>
            <Badge variant="outline" className="ml-auto">
              {validAnalyses.length} {t('components.imageAnalysis:review.analyses') || 'analyses'}
            </Badge>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-[60vh] px-6">
            {/* Summary Section */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-gray-900">
                    {t('components.imageAnalysis:review.summary') || 'Summary'}
                  </h3>
                </div>
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border text-sm font-medium ${getRiskLevelColor(highestRisk)}`}>
                  {getRiskIcon(highestRisk)}
                  {t('components.imageAnalysis:review.highestRisk') || 'Highest Risk'}: {highestRisk}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="use-all-for-ticket"
                  checked={useAllForTicket}
                  onCheckedChange={(checked) => setUseAllForTicket(checked === true)}
                />
                <label
                  htmlFor="use-all-for-ticket"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('components.imageAnalysis:review.useAllForTicket') || 'Use all for ticket description'}
                </label>
              </div>
            </div>

            <Separator className="mb-6" />

            {/* Individual Analyses */}
            <div className="space-y-6">
              {validAnalyses.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-4 mb-4">
                    {/* Image Thumbnail */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.imageUrl}
                        alt={`Analysis ${index + 1}`}
                        className="w-24 h-24 object-cover rounded-md border border-gray-200"
                      />
                    </div>

                    {/* Analysis Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          {t('components.imageAnalysis:review.analysis') || 'Analysis'} {index + 1}
                        </Badge>
                        {item.analysis?.riskLevel && (
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getRiskLevelColor(item.analysis.riskLevel)}`}>
                            {getRiskIcon(item.analysis.riskLevel)}
                            {item.analysis.riskLevel}
                          </div>
                        )}
                        {item.analysis?.confidence && (
                          <Badge variant="outline" className="text-xs">
                            {item.analysis.confidence}% {t('components.imageAnalysis:overview.confidence') || 'confidence'}
                          </Badge>
                        )}
                      </div>

                      {/* Quick Stats */}
                      <div className="flex gap-4 text-xs text-gray-600 mb-3">
                        {item.analysis?.detectedDefects?.length > 0 && (
                          <span>
                            {item.analysis.detectedDefects.length} {t('components.imageAnalysis:sections.defects') || 'defects'}
                          </span>
                        )}
                        {item.analysis?.safetyRisks?.length > 0 && (
                          <span>
                            {item.analysis.safetyRisks.length} {t('components.imageAnalysis:sections.safety') || 'risks'}
                          </span>
                        )}
                      </div>

                      {/* Editable Description */}
                      <Textarea
                        value={item.editedDescription || ''}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        className="min-h-[80px] text-sm mb-3"
                        placeholder={t('components.imageAnalysis:sections.reportPlaceholder') || 'AI analysis report...'}
                      />

                      {/* Options */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`use-ticket-${index}`}
                            checked={item.useForTicket || false}
                            onCheckedChange={(checked) => handleUseForTicketChange(index, checked === true)}
                          />
                          <label
                            htmlFor={`use-ticket-${index}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {t('components.imageAnalysis:options.useForTicketDescription') || 'Use for ticket description'}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`save-image-${index}`}
                            checked={item.saveAsImageDesc !== undefined ? item.saveAsImageDesc : true}
                            onCheckedChange={(checked) => handleSaveAsImageDescChange(index, checked === true)}
                          />
                          <label
                            htmlFor={`save-image-${index}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {t('components.imageAnalysis:options.saveAsImageDescription') || 'Save as image description'}
                          </label>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAnalysis(index)}
                          className="ml-auto text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t('components.imageAnalysis:review.remove') || 'Remove'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter className="p-6 pt-4 border-t border-gray-200">
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleIgnoreAll}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              {t('components.imageAnalysis:review.ignoreAll') || 'Ignore All'}
            </Button>
            <Button
              onClick={handleSaveSelected}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('components.imageAnalysis:review.saveSelected') || 'Save Selected'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

